import React, { FC, useEffect, useState } from 'react'
import allytm from '@ally/allytm'
import { creditCard, getDataLoadState } from '@ally/data-selectors-afg-accounts'
import { LoadState } from '@ally/use-data-domain'
import { Link } from 'react-router-dom'
import { Link as MUILink } from '@ally/metronome-ui'
import { findSSOMatch } from '../utils/findSSOMatch'
import { Loader } from './Loader'
import { Error as ErrorComponent } from './Error'
import { createSSO } from '../utils/createSSO'
import useHiddenForm from '../utils/useHiddenForm'
import {
  cardWorksSamlBaseUrl,
  CC_PREFIX,
  invalidCardStatuses,
} from '../constants'
import { useHostServices } from '../HostServices'
import AccountSelector from '../utils/AccountSelector'

type ValidPath = `${typeof CC_PREFIX}${string}`

const CreditCardRouteMap: Record<ValidPath, string> = {
  [`${CC_PREFIX}Account/FICOSCORE`]: 'fico-view',
  [`${CC_PREFIX}Account/OnlineStatement`]: 'account-statements',
  [`${CC_PREFIX}Account/Overview`]: 'account-overview',
  [`${CC_PREFIX}AccountMgmt/AddAuthorizedUser`]: 'add-authorized-user',
  [`${CC_PREFIX}AccountMgmt/Alerts`]: 'account-alerts',
  [`${CC_PREFIX}AccountMgmt/ChangePaymentDue`]: 'change-payment-due',
  [`${CC_PREFIX}AccountMgmt/PaperlessDisclosure`]: 'paperless',
  [`${CC_PREFIX}AccountMgmt/RedeemRewards`]: 'redeem-rewards',
  [`${CC_PREFIX}AccountMgmt/TravelNotification`]: 'travel-notifications',
  [`${CC_PREFIX}Activate/Activate`]: 'activate-card',
  [`${CC_PREFIX}BT/BalanceTransfer`]: 'balance-transfer',
  [`${CC_PREFIX}Enroll/Enroll`]: 'enroll',
  [`${CC_PREFIX}ePay/MakePayment`]: 'one-time-payment',
  [`${CC_PREFIX}ePay/Register`]: 'payment-account',
  [`${CC_PREFIX}ePay/SetupRecurring`]: 'auto-payment',
  [`${CC_PREFIX}Resolution/ViewOffer?ID=PayHelp`]: 'offer-payment-help',
  [`${CC_PREFIX}Resolution/ViewOffer?ID=PayPlan`]: 'offer-payment-plan',
  [`${CC_PREFIX}Resolution/ViewOffer?ID=Settlement`]: 'offer-settlement',
  [`${CC_PREFIX}Resolution/ViewOffer?ID=ShortTermProgram`]: 'offer-short-term',
  [`${CC_PREFIX}Service/AccountNotification`]: 'account-messages',
  [`${CC_PREFIX}Service/DocumentUpload`]: 'upload',
  [`${CC_PREFIX}Service/RequestPin`]: 'atm-pin',
}

interface statusType {
  type: 'LOADING' | 'SELECTING' | 'FAILURE'
  accountProxyId: string | null
}

const CARD_SELECTION_ERROR_MESSAGE = 'Select a card to continue.'

/**
 * Card Works component that delegates finding/matching the pathname to the
 * SSO map, showing a loader to the user, and then programmatically posting
 * a form with the SAML response to SSO into Card Works site
 */
export const CardWorks: FC = () => {
  const { destination, isMatch } = findSSOMatch(CreditCardRouteMap)
  const urlParams = new URLSearchParams(window.location.search)
  const [status, setStatus] = useState<statusType>({
    type: 'LOADING',
    accountProxyId: urlParams.get('accountProxyId'),
  })
  useEffect(() => {
    if (!isMatch) {
      setStatus(current => ({
        ...current,
        type: 'FAILURE',
      }))
    }
  }, [isMatch])
  const {
    session,
    hostData,
    log: { track },
  } = useHostServices()
  const { HiddenForm, submitForm } = useHiddenForm()
  /**
   * Ignoring optional chaining operator as Jest has issues with coverage
   */
  /* istanbul ignore next */
  const accessToken = session?.data?.access_token

  useEffect(() => {
    if (
      destination &&
      status.accountProxyId &&
      accessToken &&
      status.type !== 'FAILURE'
    ) {
      track('ally-next-remote-sso-credit-card-init')

      createSSO({
        destination,
        accountProxyId: status.accountProxyId,
        accessToken,
      })
        .then(response => {
          const { isSuccessful, samlAssertion, sid } =
            response.data.createSSOForCWS

          if (isSuccessful === false) {
            throw Error('Error generating saml payload')
          }

          submitForm({
            url: `${cardWorksSamlBaseUrl}/${sid}`,
            values: {
              SAMLResponse: samlAssertion,
              RelayState: 'SSO',
            },
          })
          track('ally-next-remote-sso-credit-card-done')
        })
        .catch(() => {
          track('ally-next-remote-sso-credit-card-error')
          setStatus(current => ({
            ...current,
            type: 'FAILURE',
          }))
        })
    }
  }, [destination, status.accountProxyId, accessToken])

  /**
   * Get the array of open credit card accounts from host data
   * If the array only has one account, set the accountProxyId
   * and proceed with SSO
   */

  const accountLoadState = getDataLoadState(hostData)
  const openCreditCards = creditCard
    .getAll(hostData)
    .filter(card => !invalidCardStatuses.includes(card.status))

  useEffect(() => {
    if (status.type !== 'LOADING') return
    if (status.accountProxyId) return
    if (accountLoadState === LoadState.FAILED) {
      setStatus(current => ({
        ...current,
        type: 'FAILURE',
      }))
      return
    }
    if (accountLoadState !== LoadState.LOADED) return

    if (openCreditCards.length === 1) {
      setStatus(current => ({
        ...current,
        accountProxyId: openCreditCards[0].id,
      }))
    } else if (openCreditCards.length === 0) {
      setStatus(current => ({
        ...current,
        type: 'FAILURE',
      }))
    } else {
      // "Page View" Analytics
      allytm.event('pageview', 'AOS:Dashboard:Credit Card Account Selector')
      setStatus(current => ({
        ...current,
        type: 'SELECTING',
      }))
    }
  }, [accountLoadState, status])

  /**
   * Handles submitting the actual SSO request
   */
  const submitSSO = (cardId: string): void => {
    const selectedCard = openCreditCards.find(account => account.id === cardId)
    // Send custom analytics event
    allytm.event('customEvent', {
      name: 'creditCardAccountSelector',
      type: 'userAttribute',
      attr: {
        card: {
          card: selectedCard?.productDescription ?? 'Credit Card',
        },
      },
    })
    setStatus(current => ({
      ...current,
      accountProxyId: cardId,
      type: 'LOADING',
    }))
  }

  /**
   * The form action is static. "SAM2" is the pathId specified in the card works docs
   * https://confluence.int.ally.com/display/IPRMCIAM/Credit+Card+SSO
   *
   * NOTE: The path id will change every two years when CardWeb updates the X.509 certificate
   *
   * An optional input can be added with an trace id for debugging. For example...
   * <input type="hidden" name="ssoTraceId" value="any-message-you-want" />
   */
  return (
    <>
      {(status.type === 'LOADING' || status.type === 'FAILURE') && (
        <Loader description="We’re gathering your credit card information." />
      )}
      {status.type === 'SELECTING' && (
        <AccountSelector
          accounts={openCreditCards}
          onSubmit={submitSSO}
          labelText="Choose a card:"
          errorMessage={CARD_SELECTION_ERROR_MESSAGE}
        />
      )}
      {status.type === 'FAILURE' && (
        <ErrorComponent
          errorMessage={
            <>
              We&apos;re having trouble redirecting you. We&apos;re working on a
              fix, so try again in a bit.{' '}
              <MUILink
                variant="link"
                text="Return to Snapshot"
                allytmln="returnToSnapshot"
              >
                <Link to="/dashboard" />
              </MUILink>
            </>
          }
        />
      )}
      <HiddenForm />
    </>
  )
}
